/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited  2023.
 */

import { CarouselSlider } from '@/components/blocks/Carousel';
import { StaticSlider } from '@/components/blocks/Carousel/StaticSlider';
import { ProductCard } from '@/components/blocks/ProductCard';
import { ProgressIndicator } from '@/components/blocks/ProgressIndicator';
import { useMerchandisingAssociation } from '@/data/Content/MerchandisingAssociation';
import { useLocalization } from '@/data/Localization';
import { ID } from '@/data/types/Basic';
import { ProductType } from '@/data/types/Product';
import { Typography, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { FC, useMemo } from 'react';

const EMPTY_MERCHASSOCS: ProductType[] = [];
export const MerchandisingAssociation: FC<{ id: ID }> = ({ id }) => {
	const { merchAssocs = EMPTY_MERCHASSOCS, loading } = useMerchandisingAssociation(id);
	const localization = useLocalization('productDetail');

	const [slides] = useMemo(() => {
		const transform = merchAssocs.map((ma) => ({
			a11y: { 'aria-label': undefined, 'aria-labelledby': ma.partNumber },
			card: <ProductCard key={ma.partNumber} product={ma} />,
		}));
		const slides = transform.map(({ card }) => card);
		const a11yProps = transform.map(({ a11y }) => a11y);
		return [slides, a11yProps];
	}, [merchAssocs]);

	const theme = useTheme();
	const lgMatch = useMediaQuery(theme.breakpoints.down('lg'));
	const mdMatch = useMediaQuery(theme.breakpoints.down('md'));
	const smMatch = useMediaQuery(theme.breakpoints.down('sm'));
	const visibleSlides = useMemo(
		() => (smMatch ? 1 : mdMatch ? 2 : lgMatch ? 3 : 4),
		[lgMatch, mdMatch, smMatch]
	);

	const disabledSliding = useMemo(() => visibleSlides >= slides.length, [visibleSlides, slides]);

	return loading ? (
		<ProgressIndicator />
	) : slides.length ? (
		<>
			<Typography variant="h4">{localization.recommendedProdTitle.t()}</Typography>
			{disabledSliding ? (
				<StaticSlider>{slides}</StaticSlider>
			) : (
				<CarouselSlider
					slides={slides}
				></CarouselSlider>
			)}
		</>
	) : null;
};
